<template>
  <div v-if="guide" class="amg mb-4">
    <v-expansion-panels v-model="panel" flat>
      <v-expansion-panel class="amg-panel">
        <v-expansion-panel-header class="amg-header">
          <div class="d-flex align-center">
            <v-icon color="info darken-1" class="mr-2">mdi-book-open-variant</v-icon>
            <div>
              <div class="amg-title">คู่มือเมนู “{{ guide.title }}”</div>
              <div class="caption grey--text text--darken-1">
                ความสำคัญ · ขั้นตอนการทำงาน · สิ่งที่ต้องตรวจ · ข้อควรระวัง
              </div>
            </div>
            <v-chip v-if="firstVisit" x-small color="info" dark class="ml-3">ใหม่สำหรับคุณ</v-chip>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <!-- ความสำคัญ -->
          <v-alert text dense color="info" border="left" class="mb-3">
            <div class="amg-purpose">{{ guide.purpose }}</div>
            <div v-if="guide.cycle" class="caption mt-1">
              <v-icon x-small color="info">mdi-calendar-sync</v-icon> รอบการทำงาน: {{ guide.cycle }}
            </div>
          </v-alert>

          <!-- คำอธิบายผู้รับผิดชอบ -->
          <div class="d-flex flex-wrap align-center mb-2">
            <span class="caption grey--text text--darken-1 mr-2">ผู้ดำเนินการ:</span>
            <v-chip
              v-for="(a, k) in actors"
              :key="k"
              x-small
              outlined
              :color="a.color"
              class="mr-1 mb-1"
            >
              <v-icon left x-small>{{ a.icon }}</v-icon>{{ a.label }}
            </v-chip>
          </div>

          <!-- กระบวนงาน -->
          <v-tabs v-if="guide.flows.length > 1" v-model="flowTab" show-arrows height="36" class="mb-2">
            <v-tab v-for="f in guide.flows" :key="f.name" class="amg-flow-tab">{{ f.name }}</v-tab>
          </v-tabs>
          <div v-else class="amg-section">{{ guide.flows[0].name }}</div>

          <v-timeline dense align-top class="amg-timeline pt-0">
            <v-timeline-item
              v-for="(s, i) in activeFlow.steps"
              :key="activeFlow.name + i"
              small
              fill-dot
              :color="actorOf(s).color"
              :icon="actorOf(s).icon"
            >
              <div class="d-flex flex-wrap align-center">
                <span class="amg-step-title mr-2">{{ s.title }}</span>
                <v-chip x-small :color="actorOf(s).color" dark class="mr-2">{{ actorOf(s).label }}</v-chip>
                <v-btn v-if="s.to" x-small text color="primary" class="px-1" :to="s.to">
                  เปิดเมนู <v-icon x-small right>mdi-open-in-app</v-icon>
                </v-btn>
              </div>
              <div v-if="s.detail" class="amg-step-detail">{{ s.detail }}</div>
              <div v-if="s.tip" class="amg-step-tip">
                <v-icon x-small color="amber darken-3">mdi-lightbulb-on-outline</v-icon> {{ s.tip }}
              </div>
            </v-timeline-item>
          </v-timeline>

          <v-row dense class="mt-2">
            <v-col v-if="guide.checklist.length" cols="12" md="6">
              <div class="amg-box">
                <div class="amg-section">
                  <v-icon small color="green darken-1">mdi-clipboard-check-outline</v-icon> สิ่งที่ต้องตรวจ
                </div>
                <div v-for="(c, i) in guide.checklist" :key="i" class="amg-li">
                  <v-icon x-small color="green darken-1" class="mr-1">mdi-checkbox-marked-circle-outline</v-icon>{{ c }}
                </div>
              </div>
            </v-col>
            <v-col v-if="guide.cautions.length" cols="12" md="6">
              <div class="amg-box amg-box--warn">
                <div class="amg-section">
                  <v-icon small color="deep-orange">mdi-alert-outline</v-icon> ข้อควรระวัง
                </div>
                <div v-for="(c, i) in guide.cautions" :key="i" class="amg-li">
                  <v-icon x-small color="deep-orange" class="mr-1">mdi-alert-circle-outline</v-icon>{{ c }}
                </div>
              </div>
            </v-col>
          </v-row>

          <template v-if="guide.faq.length">
            <div class="amg-section mt-3">
              <v-icon small color="blue-grey">mdi-help-circle-outline</v-icon> คำถามที่พบบ่อย
            </div>
            <div v-for="(f, i) in guide.faq" :key="i" class="amg-faq">
              <div class="font-weight-medium">ถาม: {{ f.q }}</div>
              <div class="grey--text text--darken-2">ตอบ: {{ f.a }}</div>
            </div>
          </template>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { ADMIN_MENU_GUIDE, ACTORS } from "./adminMenuGuide";

// จำว่าผู้ใช้ (ในเบราว์เซอร์นี้) เคยเปิดคู่มือแท็บไหนแล้ว — เปิดให้อัตโนมัติเฉพาะครั้งแรก
// เหมาะกับ Admin ที่เพิ่งรับหน้าที่ใหม่ ไม่ใช่ข้อมูลสำคัญ จึงเก็บใน localStorage ได้
const SEEN_KEY = "hrvec_admin_guide_seen";

function readSeen() {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY)) || {};
  } catch (e) {
    return {};
  }
}

export default {
  name: "AdminMenuGuide",

  props: {
    tabId: { type: String, required: true }
  },

  data() {
    return {
      actors: ACTORS,
      panel: undefined,
      flowTab: 0,
      firstVisit: false
    };
  },

  computed: {
    guide() {
      return ADMIN_MENU_GUIDE[this.tabId] || null;
    },
    activeFlow() {
      return this.guide.flows[this.flowTab] || this.guide.flows[0];
    }
  },

  created() {
    const seen = readSeen();
    if (this.guide && !seen[this.tabId]) {
      this.firstVisit = true;
      this.panel = 0;
      seen[this.tabId] = 1;
      try {
        localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
      } catch (e) {
        // ไม่มี storage (private mode) — แค่ไม่จำ ไม่กระทบการใช้งาน
      }
    }
  },

  methods: {
    actorOf(step) {
      return ACTORS[step.actor] || ACTORS.central;
    }
  }
};
</script>

<style scoped>
.amg-panel {
  border: 1px solid rgba(2, 136, 209, 0.25) !important;
  border-radius: 12px !important;
  overflow: hidden;
}
.amg-header {
  background: #f3f9fd;
}
.amg-title {
  font-weight: 700;
  color: #01579b;
  font-size: 1rem;
}
.amg-purpose {
  color: #263238;
  line-height: 1.6;
}
.amg-section {
  font-weight: 600;
  color: #37474f;
  margin-bottom: 6px;
}
.amg-flow-tab {
  text-transform: none;
  font-size: 0.85rem;
}
.amg-timeline {
  margin-left: -8px;
}
.amg-step-title {
  font-weight: 600;
  color: #212121;
}
.amg-step-detail {
  font-size: 0.87rem;
  color: #546e7a;
  margin-top: 2px;
}
.amg-step-tip {
  font-size: 0.8rem;
  color: #8d6e63;
  background: #fff8e1;
  border-radius: 6px;
  padding: 4px 8px;
  margin-top: 4px;
}
.amg-box {
  background: #f1f8e9;
  border-radius: 10px;
  padding: 10px 12px;
  height: 100%;
}
.amg-box--warn {
  background: #fbe9e7;
}
.amg-li {
  font-size: 0.87rem;
  margin-bottom: 4px;
  line-height: 1.5;
}
.amg-faq {
  font-size: 0.87rem;
  border-left: 3px solid #cfd8dc;
  padding: 2px 10px;
  margin-bottom: 8px;
}
</style>
